import {
  actionsKeys,
  capitalize,
  getRequiredFields,
  invoiceActionStatus,
  invoiceParkStatus,
  permissionActions,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { schema } from "./validations";
import {
  ALT_GL_ACCOUNT_ID,
  AMOUNT,
  APP_AMOUNT,
  APPROVAL_STATUS,
  APPROVED_AMOUNT,
  APPROVED_RATE_PER_UNIT,
  APPROVED_UNIT,
  ATTACHMENT,
  ATTACHMENT_ID,
  BASELINE_DATE,
  BLOCK,
  BLOCK_BY,
  BLOCK_DATE,
  BUDGET_END_MONTH,
  BUDGET_MONTH,
  BUDGET_START_MONTH,
  BUDGET_YEAR,
  CHECK,
  COMMENT,
  DOC_STATUS,
  DOCUMENT_ID,
  DOCUMENT_TYPE,
  DRCR,
  DUE_DATE,
  GL_ACCOUNT_ID,
  INVOICE_AMOUNT,
  INVOICE_CURRENCY,
  INVOICE_DATE,
  INVOICE_NUMBER,
  INVOICE_REFERENCE,
  ITEM_TEXT,
  LINE_ITEM_COMMENTS,
  LINE_ITEM_TYPE,
  LINE_ITEMS,
  PAYMENT_BLOCK,
  PAYMENT_METHODS,
  PAYMENT_TERMS,
  POST_DOCUMENT_ID,
  POSTING_DATE,
  POSTING_MONTH,
  PROPERTY,
  RATE_PER_UNIT,
  UNIT,
  VENDOR_ID,
  YEAR,
  ZONE,
} from "./consts";
import { toast } from "@/components/Toast";
import moment from "moment";
import useApi from "./useApi";
import { useQueryClient } from "@tanstack/react-query";
import { useGetPropertiesByCommunity, useGetPropertyBlockByCommunityId, useGetPropertyBlockDropdown, useGetPropertyZoneDropdown } from "@/services";

const useAddEditInvoice = () => {
  const { invoiceId, invoiceAction, action } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromDashboard = location.pathname?.includes("/dashboard");
  const queryClient = useQueryClient();
  const attachmentRef = useRef(null);
  const [lineItemCount, setLineItemCount] = useState<number>(invoiceId ? 0 : 3);
  const [openCommentDialog, setOpenCommentDialog] = useState<boolean>(false);
  const [openApprovalDialog, setOpenApprovalDialog] = useState<boolean>(false);
  const [openPaymentBlockDialog, setOpenPaymentBlockDialog] =
    useState<boolean>(false);
  const [currentAction, setCurrentAction] = useState<number>(1);
  const [isApproved, setIsApproved] = useState<boolean>(false);
  const [selectedZoneId, setSelectedZoneId] = useState<string>("");

  const [keys, setKeys] = useState({
    glMasterListing: Math.random() * 2,
    alGlMasterListing: Math.random() * 2,
    documentTypes: Math.random() * 2,
    vendorList: Math.random() * 2,
    paymentMethod: Math.random() * 2,
    paymentTerms: Math.random() * 2,
    drcr: Math.random() * 2,
    zone: Math.random() * 2,
    block: Math.random() * 2,
    property: Math.random() * 2,
  });

  const [openViewCommentDialog, setOpenViewCommentDialog] =
    useState<boolean>(false);

  const {communityData: communityDetail, loggedInUserData, accessPermission} = useSelector(
    (state: RootState) => state.currentData
  );

  const form = useForm<any>({
    defaultValues: {
      [DOCUMENT_ID]: "",
      [DOCUMENT_TYPE]: "",
      [VENDOR_ID]: "",
      [INVOICE_NUMBER]: "",
      [INVOICE_DATE]: "",
      [BASELINE_DATE]: "",
      [INVOICE_REFERENCE]: "",
      [INVOICE_AMOUNT]: "",
      [INVOICE_CURRENCY]: "USD",
      [APPROVED_AMOUNT]: "",
      [PAYMENT_TERMS]: "",
      [DUE_DATE]: "",
      [PAYMENT_METHODS]: "",
      [POSTING_DATE]: moment(),
      [POSTING_MONTH]: moment().format("MMM"),
      [YEAR]: moment().format("YYYY"),
      [PAYMENT_BLOCK]: false,
      [BLOCK_DATE]: "",
      [BLOCK_BY]: loggedInUserData?.fullName,
      [ATTACHMENT]: "",
      [COMMENT]: "",
      [LINE_ITEM_COMMENTS]: "",
      [LINE_ITEMS]: Array.from({ length: lineItemCount }, () => ({
        [GL_ACCOUNT_ID]: "",
        [ALT_GL_ACCOUNT_ID]: "",
        [DRCR]: "DR",
        [UNIT]: "",
        [RATE_PER_UNIT]: "",
        [AMOUNT]: "",
        [APPROVED_UNIT]: "",
        [APPROVED_RATE_PER_UNIT]: "",
        [APP_AMOUNT]: "",
        [BUDGET_MONTH]: "",
        [BUDGET_YEAR]: "",
        [ITEM_TEXT]: "",
        [APPROVAL_STATUS]: "",
        [ZONE]: "all",
        [BLOCK]: "all",
        [PROPERTY]: "all",
      })),
    },
    resolver: zodResolver(schema),
  });

  const requiredFields = getRequiredFields(schema);

  const { data: zoneList, isLoading: isLoadingZoneList } = useGetPropertyZoneDropdown({
    communityId: communityDetail?.communityId,
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  const { data: propertyBlock, isLoading: isBlockLoading } =
    useGetPropertyBlockDropdown({
      communityId: communityDetail?.communityId,
      zoneId: selectedZoneId,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      }
    });

  const { data: propertyBlockZoneList } = useGetPropertyBlockByCommunityId({
    communityId: communityDetail?.communityId,
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  const { data: propertyList, isLoading: isPropertyLoading } =
    useGetPropertiesByCommunity({
      communityId: communityDetail?.communityId,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      }
    });

  const onZoneChange = (zoneId: string, index: number) => {
    setSelectedZoneId(zoneId == "all" ? "" : zoneId);
    form.setValue(`${LINE_ITEMS}.${index}.${BLOCK}`, "all");
  };

  const onPropertyChange = (propertyId: string, index: number) => {
    const selectedProperty = propertyList?.data?.find(
      (data: any) => data?.propertyId?.toString() == propertyId
    );
    const selectedBlock = propertyBlockZoneList?.data?.find(
      (data: any) => data?.blockId?.toString() == selectedProperty?.blockId?.toString()
    );
    const zoneId = selectedBlock?.zoneId?.toString() || "all";

    setSelectedZoneId(zoneId == "all" ? "" : zoneId);
    form.setValue(`${LINE_ITEMS}.${index}.${BLOCK}`, selectedProperty?.blockId?.toString() || "all");
    form.setValue(`${LINE_ITEMS}.${index}.${ZONE}`, zoneId);
  };

  const invoicePermission = useMemo(() => accessPermission[actionsKeys.INVOICE?.toLowerCase()], [accessPermission])
  const isApprovalPending = invoiceAction == invoiceActionStatus.pending_for_cam_approval && invoicePermission[permissionActions.APPROVE]

  useEffect(() => {
    queryClient.removeQueries({ queryKey: ["park-document", invoiceId] });
    queryClient.removeQueries({ queryKey: ["posted-document", invoiceId] });
  }, []);

  const breadcrumbDataActivity = useMemo(() => {
    return invoiceId
      ? [
          {
            link: `/${routes.cam}/${routes.activityDashboard}`,
            name: `Activity Dashboard - ${communityDetail?.name || ""}`,
          },
          {
            link: `/${routes.cam}/${routes.activityDashboard}/${routes.invoice}/${invoiceAction}`,
            name: (
              <span>
                IV
                {invoiceParkStatus[invoiceAction || ""]
                  ? " - " + invoiceParkStatus[invoiceAction || ""]?.label
                  : ""}
              </span>
            ),
          },
          {
            link: "",
            name: (
              <span className="text-primary">{`${capitalize(
                action || ""
              )}`}</span>
            ),
          },
        ]
      : [
          {
            link: `/${routes.cam}/${routes.activityDashboard}`,
            name: `Activity Dashboard - ${communityDetail?.name || ""}`,
          },
          {
            link: "",
            name: <span className="text-primary">{"Create"}</span>,
          },
        ];
  }, []);

  const breadcrumbDataDashboard = useMemo(() => {
    return [
      {
        link: `/${routes.cam}/${routes.dashboard}`,
        name: `Dashboard - ${communityDetail?.name || ""}`,
      },
      {
        link: `/${routes.cam}/${routes.dashboard}/${routes.invoice}/${invoiceAction}`,
        name: (
          <span>
            IV
            {invoiceParkStatus[invoiceAction || ""]
              ? " - " + invoiceParkStatus[invoiceAction || ""]?.label
              : ""}
          </span>
        ),
      },
      {
        link: "",
        name: (
          <span className="text-primary">{`${capitalize(action || "")}`}</span>
        ),
      },
    ];
  }, []);

  const breadcrumbData = fromDashboard
    ? breadcrumbDataDashboard
    : breadcrumbDataActivity;

  const breadcrumbTitle = invoiceId
    ? `${capitalize(action || "")} Vendor Invoice - ${communityDetail?.name}`
    : `Create Invoice - ${communityDetail?.name}`;

  // const {
  //   data: communityDtl,
  //   isLoading: isCommunityDtlLoading,
  // } = useGetCommunityReview({
  //   community_id: communityDetail?.communityId,
  //   queryParams: {
  //     enabled: !!(communityDetail?.communityId && isNotAdd),
  //   },
  // });

  const onBackClickAdmin = () => {
    invoiceId
      ? navigate(
          `/${routes.cam}/${routes.activityDashboard}/${routes.invoice}/${invoiceAction}`
        )
      : navigate(`/${routes.cam}/${routes.activityDashboard}`);
  };

  const onBackToPaymentList = () => {
    navigate(
      `/${routes.cam}/${
        fromDashboard ? routes.dashboard : routes.activityDashboard
      }/${routes.payment}/post`
    );
  };

  const onBackToReport = () => {
    navigate(
      `/${routes.cam}/${
        fromDashboard ? routes.dashboard : routes.activityDashboard
      }/${routes.reports}`
    );
  };

  const onBackClick =
    location?.state?.from == "payment-listing"
      ? onBackToPaymentList
      : location?.state?.from == "reports"
      ? onBackToReport
      : onBackClickAdmin;

  const formReset = () => {
    const documentTypeId = documentTypes
      ?.find((data: any) => {
        return data?.documentType?.toLowerCase() == "vi";
      })
      ?.documentTypeId?.toString();

    setLineItemCount(3);
    form.reset({
      [DOCUMENT_ID]: "",
      [DOCUMENT_TYPE]: documentTypeId,
      [VENDOR_ID]: "",
      [INVOICE_NUMBER]: "",
      [INVOICE_DATE]: "",
      [BASELINE_DATE]: "",
      [INVOICE_REFERENCE]: "",
      [INVOICE_AMOUNT]: "",
      [INVOICE_CURRENCY]: "USD",
      [APPROVED_AMOUNT]: "",
      [PAYMENT_TERMS]: "",
      [DUE_DATE]: "",
      [PAYMENT_METHODS]: "",
      [POSTING_DATE]: moment(),
      [POSTING_MONTH]: moment().format("MMM"),
      [YEAR]: moment().format("YYYY"),
      [PAYMENT_BLOCK]: false,
      [BLOCK_DATE]: "",
      [BLOCK_BY]: loggedInUserData?.fullName,
      [ATTACHMENT]: "",
      [COMMENT]: "",
      [LINE_ITEM_COMMENTS]: "",
      [LINE_ITEMS]: Array.from({ length: 3 }, () => ({
        [GL_ACCOUNT_ID]: "",
        [ALT_GL_ACCOUNT_ID]: "",
        [DRCR]: "DR",
        [UNIT]: "",
        [RATE_PER_UNIT]: "",
        [AMOUNT]: "",
        [APPROVED_UNIT]: "",
        [APPROVED_RATE_PER_UNIT]: "",
        [APP_AMOUNT]: "",
        [BUDGET_MONTH]: "",
        [BUDGET_YEAR]: "",
        [ITEM_TEXT]: "",
        [APPROVAL_STATUS]: "",
        [ZONE]: "all",
        [BLOCK]: "all",
        [PROPERTY]: "all",
      })),
    });
  };

  const {
    vendorList,
    isVendorLoading,
    documentTypes,
    isDocumentTypeLoading,
    addInfo,
    isAddPending,
    updateInfo,
    isUpdatePending,
    docDetails,
    isDetailLoading,
    isAddSubmitForApprovalPending,
    lineItemComment,
    setLineItemComment,
    isAddCommentPending,
    lineItem,
    setLineItem,
    lineItemCommentText,
    approveInfo,
    isApprovePending,
    rejectInfo,
    isRejectPending,
    postInfo,
    isPostingPending,
    openApproveRejectDialog,
    setOpenApproveRejectDialog,
    openPostDialog,
    setOpenPostDialog,
    glAccount,
    isGLAccountLoading,
    paymentMethodOptions,
    isPaymentMethodLoading,
    paymentTermOptions,
    isPaymentTermLoading,
    setDeletedLineItem,
    deletedLineItem,
    deleteLineItemInfo,
    openDeleteLineItemDialog,
    setOpenDeleteLineItemDialog,
    isAttachmentPending,
    isDeletePending,
    isAttachmentLoading,
    attachment,
    isPosted,
    reassignInfo,
    isReassignPending,
    isEntryView,
    setIsEntryView
  } = useApi({ form, onBackClick, currentAction, formReset });

  const glRows = useMemo(() => {
    const payload = glAccount?.data ?? glAccount;
    return Array.isArray(payload) ? payload : [];
  }, [glAccount]);

  const getAltGlNumber = (item: any) => {
    const fromItem = item?.alternativeAccount ?? item?.alternativeAccountNumber;
    if (fromItem !== undefined && fromItem !== null && `${fromItem}` !== "") {
      const byId = glRows.find(
        (gl: any) => gl?.glMasterId?.toString() === fromItem.toString()
      );
      return (byId?.glAccountNumber ?? fromItem).toString();
    }
    const selectedGl = glRows.find(
      (gl: any) =>
        gl?.glMasterId?.toString() === item?.[GL_ACCOUNT_ID]?.toString()
    );
    if (selectedGl?.alternativeAccount) {
      return selectedGl.alternativeAccount.toString();
    }
    const altGl = glRows.find(
      (gl: any) =>
        gl?.glMasterId?.toString() === item?.alternativeAccountId?.toString()
    );
    return altGl?.glAccountNumber?.toString() || "";
  };

  useEffect(() => {
    if (attachment) {
      form.setValue(
        ATTACHMENT,
        attachment?.data?.[attachment?.data?.length - 1]
          ? {
              ...attachment?.data?.[attachment?.data?.length - 1],
              name:
                attachment?.data?.[attachment?.data?.length - 1]
                  ?.originalFileName || "",
              size:
                attachment?.data?.[attachment?.data?.length - 1]?.fileSize ||
                "0",
            }
          : null
      );
    }
  }, [attachment]);

  useEffect(() => {
    if (docDetails) {
      form.reset({
        [ATTACHMENT]: form.watch(ATTACHMENT),
        [DOCUMENT_ID]:
          isPosted ? docDetails?.data?.header?.[POST_DOCUMENT_ID]?.toString() : docDetails?.data?.header?.[DOCUMENT_ID]?.toString() || "",
        [DOCUMENT_TYPE]:
          docDetails?.data?.header?.[DOCUMENT_TYPE]?.toString() || null,
        [VENDOR_ID]: docDetails?.data?.header?.[VENDOR_ID]?.toString() || null,
        [INVOICE_NUMBER]:
          docDetails?.data?.header?.[INVOICE_NUMBER]?.toString(),
        [INVOICE_DATE]: docDetails?.data?.header?.[INVOICE_DATE],
        [BASELINE_DATE]: docDetails?.data?.header?.[BASELINE_DATE],
        [INVOICE_REFERENCE]:
          docDetails?.data?.header?.[INVOICE_REFERENCE] || "",
        [INVOICE_AMOUNT]:
          docDetails?.data?.header?.[INVOICE_AMOUNT]?.toString(),
        [INVOICE_CURRENCY]: docDetails?.data?.header?.[INVOICE_CURRENCY],
        [APPROVED_AMOUNT]:
          docDetails?.data?.header?.[APPROVED_AMOUNT]?.toString(),
        [PAYMENT_TERMS]: paymentTermOptions?.data
          ?.find((term: any) => {
            const _paymentTerm = (vendorList || [])
              ?.find(
                (vendor: any) =>
                  vendor.vendorId?.toString() ===
                  docDetails?.data?.header?.[VENDOR_ID]?.toString()
              )
              ?.paymentTerm?.toString();

            return term?.paymentTermId?.toString() == _paymentTerm;
          })
          ?.paymentTermName?.toString(),
        [DUE_DATE]: docDetails?.data?.header?.[DUE_DATE],
        [PAYMENT_METHODS]: paymentMethodOptions?.data
          ?.find((method: any) => {
            const _paymentMethod = (vendorList || [])
              ?.find(
                (vendor: any) =>
                  vendor.vendorId?.toString() ===
                  docDetails?.data?.header?.[VENDOR_ID]?.toString()
              )
              ?.paymentMethod?.toString();

            return method?.paymentMethodId?.toString() == _paymentMethod;
          })
          ?.methodName?.toString(),
        [POSTING_DATE]: docDetails?.data?.header?.[POSTING_DATE],
        [POSTING_MONTH]: docDetails?.data?.header?.[POSTING_DATE]
          ? moment(docDetails?.data?.header?.[POSTING_DATE]).format("MMM")
          : undefined,
        [YEAR]: docDetails?.data?.header?.[POSTING_DATE]
          ? moment(docDetails?.data?.header?.[POSTING_DATE]).format("YYYY")
          : undefined,
        [PAYMENT_BLOCK]: docDetails?.data?.header?.[PAYMENT_BLOCK] || false,
        [BLOCK_DATE]: docDetails?.data?.header?.[BLOCK_DATE],
        [BLOCK_BY]: docDetails?.data?.header?.[BLOCK_BY] || "",
        [ATTACHMENT_ID]: docDetails?.data?.header?.[ATTACHMENT_ID],
        [COMMENT]: docDetails?.data?.header?.[COMMENT] || "",
        [DOC_STATUS]: docDetails?.data?.header?.[DOC_STATUS],
        [LINE_ITEMS]: Array.isArray(docDetails?.data?.[LINE_ITEMS])
          ? docDetails?.data?.[LINE_ITEMS]?.map((item: any) => {
              return {
                ...item,
                [GL_ACCOUNT_ID]: item?.[GL_ACCOUNT_ID]?.toString() || "",
                [ALT_GL_ACCOUNT_ID]: getAltGlNumber(item),
                [DRCR]: item?.[DRCR]?.toUpperCase() || "",
                [UNIT]: item?.[UNIT]?.toString() || "",
                [RATE_PER_UNIT]: item?.[RATE_PER_UNIT]?.toString() || "",
                [AMOUNT]:
                  Math.round(item?.[UNIT] * item?.[RATE_PER_UNIT])?.toString() || "",
                [APPROVED_UNIT]: item?.[APPROVED_UNIT]?.toString() || "",
                [APPROVED_RATE_PER_UNIT]:
                  item?.[APPROVED_RATE_PER_UNIT]?.toString() || "",
                [APP_AMOUNT]:
                  Math.round(
                    item?.[APPROVED_UNIT] * item?.[APPROVED_RATE_PER_UNIT]
                  )?.toString() || "",
                [BUDGET_MONTH]: item?.[BUDGET_START_MONTH]
                  ? {
                      from: new Date(
                        item?.[BUDGET_YEAR],
                        Number(item?.[BUDGET_START_MONTH] - 1),
                        1
                      ),
                      to: new Date(
                        item?.[BUDGET_YEAR],
                        Number(item?.[BUDGET_END_MONTH] - 1),
                        1
                      ),
                    }
                  : undefined,
                [BUDGET_YEAR]: item?.[BUDGET_YEAR]?.toString() || "",
                [ITEM_TEXT]: item?.[ITEM_TEXT]?.toString() || "",
                [APPROVAL_STATUS]: item?.[APPROVAL_STATUS]?.toString() || "",
                [ZONE]: item?.[ZONE]?.toString() || "all",
                [BLOCK]: item?.[BLOCK]?.toString() || "all",
                [PROPERTY]: item?.[PROPERTY]?.toString() || "all",
              };
            })
          : [],
      });

      setKeys({
        glMasterListing: Math.random() * 2,
        alGlMasterListing: Math.random() * 2,
        documentTypes: Math.random() * 2,
        vendorList: Math.random() * 2,
        paymentMethod: Math.random() * 2,
        paymentTerms: Math.random() * 2,
        drcr: Math.random() * 2,
        zone: Math.random() * 2,
        block: Math.random() * 2,
        property: Math.random() * 2,
      });
    }
  }, [docDetails, isEntryView, glRows]);

  useEffect(() => {
    if (documentTypes) {
      if (!invoiceId) {
        const documentTypeId = documentTypes
          ?.find((data: any) => {
            return data?.documentType?.toLowerCase() == "vi";
          })
          ?.documentTypeId?.toString();
        if (documentTypeId) {
          form.setValue(DOCUMENT_TYPE, documentTypeId);
        } else {
          toast.error(
            "The Document Type 'VI' was not found. Kindly add it to proceed."
          );
          onBackClick();
        }
      }
      setKeys((prev: any) => ({ ...prev, documentTypes: Math.random() * 4 }));
    }
  }, [documentTypes]);

  useEffect(() => {
    if (vendorList) {
      setKeys((prev: any) => ({ ...prev, vendorList: Math.random() * 4 }));
    }
  }, [vendorList]);

  // submit for approval
  const onApprovalConfirm = () => {
    onSubmit(form.watch());
  };

  //Park
  const onSubmit = (data: any) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    const isBudgetMonthMissing = (_data[LINE_ITEMS] as any[])
      ?.filter((item) => item[GL_ACCOUNT_ID])
      .map((item) => setEmptyFieldsToNull(item))
      .some((item) => !item[BUDGET_MONTH]);

    if (isBudgetMonthMissing) {
      form.setError(BUDGET_MONTH, {
        message: "Budget Month and Year is required",
      });
      setOpenApprovalDialog(false);
      return;
    }

    const isBudgetYearsDiffer = (_data[LINE_ITEMS] as any[])
      ?.filter((item) => item[GL_ACCOUNT_ID])
      .map((item) => setEmptyFieldsToNull(item))
      .some((item) => {
        const fromYear = moment(item[BUDGET_MONTH]?.from).format('YYYY');
        const toYear = moment(item[BUDGET_MONTH]?.to).format('YYYY');

        return (Number(toYear) - Number(fromYear) > 0);
      });

    if (isBudgetYearsDiffer) {
      form.setError(BUDGET_MONTH, {
        message: "Please ensure that the budget start and end year are the same.",
      });
      setOpenApprovalDialog(false);
      return;
    }
    
    const isQtyLessThanMonth = (_data[LINE_ITEMS] as any[])
      ?.filter((item) => item[GL_ACCOUNT_ID])
      .map((item) => setEmptyFieldsToNull(item))
      .some((item) => {
        const fromDate = moment(item[BUDGET_MONTH]?.from);
        const toDate = moment(item[BUDGET_MONTH]?.to);

        // Inclusive difference (add +1 if needed)
        const totalMonths = toDate.diff(fromDate, "months") + 1;
        return totalMonths > item[APPROVED_UNIT];
      });

    if (isQtyLessThanMonth) {
      form.setError(BUDGET_MONTH, {
        message: "Please adjust Approved QTY as per Budget Month and Year.",
      });
      setOpenApprovalDialog(false);
      return;
    }

    const lineItems = (_data[LINE_ITEMS] as any)
      ?.filter((items: any) => items[GL_ACCOUNT_ID])
      .map((data: any) => setEmptyFieldsToNull(data))
      ?.map((data: any) => {
        data[AMOUNT] = data[APP_AMOUNT];
        delete data[APP_AMOUNT];
        return {
          ...data,
          [ZONE]: data[ZONE] == "all" ? "" : data[ZONE],
          [BLOCK]: data[BLOCK] == "all" ? "" : data[BLOCK],
          [PROPERTY]: data[PROPERTY] == "all" ? "" : data[PROPERTY],
          [ITEM_TEXT]: data[ITEM_TEXT] ?? " ",
          [BUDGET_YEAR]: data[BUDGET_MONTH]
            ? moment(data[BUDGET_MONTH]?.from).format("YYYY")
            : null,
          [BUDGET_START_MONTH]: data[BUDGET_MONTH]
            ? moment(data[BUDGET_MONTH]?.from).format("MM")
            : null,
          [BUDGET_END_MONTH]: data[BUDGET_MONTH]
            ? moment(data[BUDGET_MONTH]?.to).format("MM")
            : null,
        };
      });

    delete _data[LINE_ITEMS];
    delete _data[POSTING_MONTH];

    _data[INVOICE_DATE] = moment(_data[INVOICE_DATE] as any).format(
      "YYYY-MM-DD"
    );
    _data[DUE_DATE] = _data[DUE_DATE]
      ? moment(_data[DUE_DATE] as any).format("YYYY-MM-DD")
      : null;
    _data[POSTING_DATE] = moment(_data[POSTING_DATE] as any).format(
      "YYYY-MM-DD"
    );
    _data[BASELINE_DATE] = moment(_data[BASELINE_DATE] as any).format(
      "YYYY-MM-DD"
    );
    const extraFields = {
      balanceAmount: 0,
    };

    setOpenApprovalDialog(false);
    if (_data[APPROVED_AMOUNT] && balanceAmount == 0) {
      if (invoiceId) {
        const header: any = {
          ..._data,
          communityId: communityDetail?.communityId,
          ...extraFields,
        };
        delete header[ATTACHMENT];
        updateInfo({
          id: invoiceId,
          data: {
            header,
            [LINE_ITEMS]: lineItems?.filter((val: any) => val?.lineItemType?.toLowerCase() == "main"),
          },
        });
      } else {
        delete _data[DOCUMENT_ID];
        const header: any = {
          ..._data,
          communityId: communityDetail?.communityId,
          ...extraFields,
        };
        delete header[ATTACHMENT];
        addInfo({
          data: {
            header,
            [LINE_ITEMS]: lineItems,
          },
        });
      }
    } else {
      toast.error(
        "Submission failed: Balance must be zero. Please adjust the approved or invoice line item amounts."
      );
      setOpenApprovalDialog(false);
      return;
    }
  };

  const onAddMore = () => {
    setLineItemCount((prev) => prev + 1);
  };

  useEffect(() => {
    if (lineItemCount != form.getValues()[LINE_ITEMS]?.length) {
      form.setValue(LINE_ITEMS, [
        ...form.getValues()[LINE_ITEMS],
        {
          [GL_ACCOUNT_ID]: "",
          [ALT_GL_ACCOUNT_ID]: "",
          [DRCR]: "",
          [UNIT]: "",
          [RATE_PER_UNIT]: "",
          [AMOUNT]: "",
          [APPROVED_UNIT]: "",
          [APPROVED_RATE_PER_UNIT]: "",
          [APP_AMOUNT]: "",
          [BUDGET_MONTH]: "",
          [BUDGET_YEAR]: "",
          [ITEM_TEXT]: "",
          [APPROVAL_STATUS]: "",
          [ZONE]: "all",
          [BLOCK]: "all",
          [PROPERTY]: "all",
        },
      ]);
    }
  }, [lineItemCount]);

  const calculateAmount = (unit: number, rate: number, amountKey: string) => {
    const amt = unit * rate;
    form.setValue(amountKey, parseFloat(amt.toString()).toFixed(2));
  };

  const lineItems = useWatch({ control: form.control, name: LINE_ITEMS });
  const approvedAmount = useWatch({
    control: form.control,
    name: APPROVED_AMOUNT,
  });

  const allDisabled = useMemo(() => {
    return action == "view";
  }, [lineItems]);

  const balanceAmount = useMemo(() => {
    if (Array.isArray(lineItems)) {
      // const finalGl = glAccount?.data?.find(
      //   (data: any) => data?.glAccountNumber == "7000"
      // );

      const lItems = lineItems?.filter((data: any) => data?.[LINE_ITEM_TYPE]?.toLowerCase() != "offset")
      const drValues = lItems
        .filter(
          (item) =>
            item[DRCR]?.toLowerCase() == "dr" &&
            (item[APP_AMOUNT] || item[AMOUNT])
            // item[GL_ACCOUNT_ID] != finalGl?.glMasterId
        )
        .reduce((sum, item) => {
          const amt = Number(item[APP_AMOUNT]) ? item[APP_AMOUNT] : item[AMOUNT] || 0
          return sum + Number(amt)
        }, 0);

      const crValues = lItems
        .filter(
          (item) =>
            item[DRCR]?.toLowerCase() == "cr" &&
            (item[APP_AMOUNT] || item[AMOUNT])
        )
        .reduce((sum, item) => {
          const amt = Number(item[APP_AMOUNT]) ? item[APP_AMOUNT] : item[AMOUNT] || 0
          return sum + Number(amt)
        }, 0);

        // if (isNotAdd) {
        //   return drValues - crValues
        // }
        return Number(Math.abs(approvedAmount || 0)) - drValues - crValues;
    }
    return 0;
  }, [lineItems, approvedAmount]);

  const commentDisabled = useMemo(() => {
    return Array.isArray(lineItems) && lineItems.some((item) => item[CHECK]);
  }, [lineItems]);

  const postDocBtnDisable = useMemo(() => {
    return (
      Array.isArray(lineItems) &&
      lineItems.some(
        (item) => item[APPROVAL_STATUS]?.toLowerCase() == "approved"
      ) &&
      docDetails?.data?.header?.documentStatus?.toLowerCase() == "approved"
    );
  }, [lineItems]);

  const onSaveComment = () => {
    if (lineItemComment) {
      form.setValue(LINE_ITEM_COMMENTS, lineItemComment);
      setOpenCommentDialog(false);
    } else {
      toast.error("Please add comments");
    }
  };

  const onCommentChange = (e: any) => {
    setLineItemComment(e.target.value);
  };

  const onBaseLineDateChanged = () => {
    const { baselineDate, vendorId } = form.watch();
    const days = paymentTermOptions?.data?.find((term: any) => {
      const _paymentTerm = (vendorList || [])
        .find(
          (vendor: any) => vendor.vendorId?.toString() === vendorId?.toString()
        )
        ?.paymentTerm?.toString();

      return term?.paymentTermId?.toString() == _paymentTerm;
    })?.days;

    if (!baselineDate) return;

    const hasDays = days !== null && days !== undefined && days !== "";
    if (!hasDays || Number(days) === 0) {
      form.setValue(DUE_DATE, moment());
    } else {
      form.setValue(DUE_DATE, moment(baselineDate).add(Number(days), "days"));
    }
  };

  const onActionClick = (action: number) => {
    setCurrentAction(action);
  };

  const onCommentClick = (lineItem: any) => {
    setLineItem(lineItem);
    setOpenViewCommentDialog(true);
  };

  const onApproveClick = () => {
    setOpenApproveRejectDialog(true);
    setIsApproved(true);
  };

  const onRejectClick = () => {
    setOpenApproveRejectDialog(true);
    setIsApproved(false);
  };

  const onApproveConfirm = (reason: string) => {
    const documentLineItemIds =
      Array.isArray(lineItems) &&
      lineItems
        .map((item) => {
          if (item[CHECK]) return item?.parkDocumentLineItemId;
        })
        ?.filter((item) => item);
    approveInfo({
      documentId: invoiceId as string,
      data: { reason, documentLineItemIds },
    });
  };

  const onRejectConfirm = (reason: string) => {
    const documentLineItemIds =
      Array.isArray(lineItems) &&
      lineItems
        .map((item) => {
          if (item[CHECK]) return item?.parkDocumentLineItemId;
        })
        ?.filter((item) => item);
    rejectInfo({
      documentId: invoiceId as string,
      data: { reason, documentLineItemIds },
    });
  };

  const onClose = () => {
    setOpenApproveRejectDialog(false);
    setIsApproved(false);
  };

  const onPostConfirmation = () => {
    postInfo({ communityId: communityDetail?.communityId as string, parkDocId: invoiceId as string });
  };

  const onSavePaymentBlock = (data: any) => {
    form.setValue(PAYMENT_BLOCK, data[PAYMENT_BLOCK]);
    form.setValue(BLOCK_DATE, data[BLOCK_DATE]);
    form.setValue(BLOCK_BY, data[BLOCK_BY]);
    setOpenPaymentBlockDialog(false);
  };

  const enabledApproveReject = invoicePermission[permissionActions.APPROVE] &&
    invoiceAction == invoiceActionStatus.pending_for_cam_approval;

  const enableCheckBox = (lineItem: any) => {
    const isCurrentUserApprover = lineItem?.approvers?.find(
      (approver: any) => approver?.userId == loggedInUserData?.userId
    );

    if (
      form.watch(DOC_STATUS)?.toLowerCase() == "PENDING_APPROVAL" &&
      isCurrentUserApprover?.approvalStatus == "PENDING_APPROVAL"
    ) {
      return true;
    } else {
      return false;
    }
  };

  const onVendorChange = (selectedValue: any) => {
    const selectedOption = (vendorList || []).find(
      (vendor: any) => vendor.vendorId?.toString() === selectedValue
    );

    if (selectedOption?.paymentTerm) {
      const term = paymentTermOptions?.data
        ?.find(
          (term: any) =>
            term?.paymentTermId?.toString() ==
            selectedOption.paymentTerm?.toString()
        )
        ?.paymentTermName?.toString();
      form.setValue(PAYMENT_TERMS, term);
    }

    if (selectedOption?.paymentMethodId) {
      const method = paymentMethodOptions?.data
        ?.find(
          (method: any) =>
            method?.paymentMethodId?.toString() ==
            selectedOption?.paymentMethodId?.toString()
        )
        ?.methodName?.toString();

      form.setValue(PAYMENT_METHODS, method);
    }

    setKeys((prev: any) => ({
      ...prev,
      paymentMethod: Math.random() * 4,
      paymentTerms: Math.random() * 4,
    }));
  };

  const onDeleteLineItem = (index: number) => {
    setOpenDeleteLineItemDialog(true);
    setDeletedLineItem(index);
  };

  const onDeleteLineItemConfirmation = () => {
    const lineItems = form.watch(LINE_ITEMS);

    if (lineItems[deletedLineItem]?.parkDocumentLineItemId) {
      deleteLineItemInfo({
        id: lineItems[deletedLineItem]?.parkDocumentLineItemId as string,
      });
    } else {
      setDeletedLineItem("");
      setOpenDeleteLineItemDialog(false);
    }
    lineItems?.splice(deletedLineItem, 1);

    form.setValue(LINE_ITEMS, lineItems);
  };

  const confirmationText = () => {
    if (invoiceAction == invoiceActionStatus.pending_for_cam_approval) {
      if (isApproved) {
        return <>Are you sure you want to approve this invoice?</>;
      } else {
        return <>Are you sure you want to reject this invoice?</>;
      }
    } else if (invoiceAction == invoiceActionStatus.pending_for_my_approval) {
      if (isApproved) {
        return (
          <>
            <b>
              Are you sure you want to approve the selected invoice line
              item(s)?
            </b>
            <br />
            <br />
            Once approved, the line item(s) will be processed according to the
            approval workflow and cannot be modified.
          </>
        );
      } else {
        return (
          <>
            <b>
              Are you sure you want to reject the selected invoice line item(s)?
            </b>
            <br />
            <br /> Once rejected, the line item(s) will be sent to the Property
            Manager for review and edits. Only the Property Manager can make
            changes and resubmit the item(s) for approval.
          </>
        );
      }
    } else if (
      invoiceAction == invoiceActionStatus.pending_for_board_approval
    ) {
      if (isApproved) {
        return (
          <>
            <b>Do you want to approve the selected invoice line item(s)? </b>{" "}
            <br />
            Some of the selected line items require Board approval. Each line
            item has its own approval requirement: <br />
            <br />
            {form
              .watch(LINE_ITEMS)
              ?.filter((item: any) => item[CHECK])
              ?.map(
                (item: any) =>
                  `Line Item ${
                    glAccountList?.find(
                      (gl: any) => gl.glMasterId == item[GL_ACCOUNT_ID]
                    )?.glAccountNumber || ""
                  }: ${
                    item.approvers?.filter(
                      (approver: any) =>
                        approver?.approvalStatus?.toLowerCase() == "approved"
                    )?.length
                  } of ${item.approvers?.length} approvals received\n`
              )}
            <br />
            <br />
            Your approval will be recorded and applied accordingly. <br />
            <br /> Note: Once the required number of Board approvals is reached
            for a line item, it will be marked as Board Approved.
          </>
        );
      } else {
        return (
          <>
            <b>
              Are you sure you want to reject the selected invoice line item(s)?
            </b>
            <br />
            1. If <b>any</b> line item is rejected,{" "}
            <b>the entire invoice will be marked as Rejected.</b>
            <br />
            <br />
            2. <b>All previous Board approvals will be reversed,</b> and the
            invoice must be <b>resubmitted by the Property Manager</b> for
            approval from the beginning. <br />
            <br />
            <b>
              {" "}
              This action cannot be undone. Do you want to proceed with
              rejection?
            </b>
          </>
        );
      }
    } else if (invoiceAction == invoiceActionStatus.pending_for_agm_approval) {
      if (isApproved) {
        return (
          <>
            <b>
              Are you sure you want to approve the selected invoice line item(s)
              tagged for AGM approval?
            </b>
            <br />
            <br />
            These line items require{" "}
            <b>approval from a minimum number of Board Members</b> and selection
            of the AGM Approved checkbox. <br />
            <br />{" "}
            {form
              .watch(LINE_ITEMS)
              ?.filter((item: any) => item[CHECK])
              ?.map(
                (item: any) =>
                  `Line Item ${
                    glAccountList?.find(
                      (gl: any) => gl.glMasterId == item[GL_ACCOUNT_ID]
                    )?.glAccountNumber || ""
                  }: ${
                    item.approvers?.filter(
                      (approver: any) =>
                        approver?.approvalStatus?.toLowerCase() == "approved"
                    )?.length
                  } of ${item.approvers?.length} approvals received\n`
              )}{" "}
            <br />
            <br /> Your approval will be recorded only if the{" "}
            <b>AGM Approved</b>
            checkbox is selected. <b>Do you want to proceed with approval?</b>
          </>
        );
      } else {
        return (
          <>
            <b>
              Are you sure you want to reject the selected invoice line item(s)?
            </b>
            <br />
            <br />
            If any line item is rejected, the{" "}
            <b>entire invoice will be marked as Rejected.</b>
            <br />
            <br />
            <b>All previous AGM approvals will be reversed,</b> and the invoice
            must be <b>resubmitted by the Property Manager</b> for approval from
            the beginning.
          </>
        );
      }
    }
  };

  const isCreate = !invoiceAction && !action;

  const isUpdatePermission = isCreate
    ? true
    : action == "edit" &&
      invoicePermission[permissionActions.UPDATE];

  const documentId = useWatch({control: form.control, name: DOCUMENT_ID});

  const glAccountList = useMemo(() => {
    const byNumber = (a: any, b: any) =>
      (a?.glAccountNumber ?? "").localeCompare(b?.glAccountNumber ?? "");
    if (isPosted) {
      return [...glRows].sort(byNumber);
    }

    return [...glRows]
      .filter((data: any) => data?.accountCategory == "E" && data?.active && !data?.alternateAccount)
      .sort(byNumber);
  }, [glRows, isPosted]);

  const onReassign = () => {
    reassignInfo({
      parkDocId: invoiceId as string,
      communityId: communityDetail?.communityId,
      data: {
        lineItemIds: lineItems?.filter((data: any) => data?.[APPROVAL_STATUS]?.toLowerCase() == "pending_approval")?.map((data: any) => data?.parkDocumentLineItemId),
        reason: "Reassign"
      }
    });
  };

  return {
    breadcrumbData,
    onBackClick,
    communityDetail,
    form,
    onSubmit,
    requiredFields,
    attachmentRef,
    onAddMore,
    calculateAmount,
    balanceAmount,
    vendorList,
    isVendorLoading,
    onCancel: onBackClick,
    commentDisabled,
    openCommentDialog,
    setOpenCommentDialog,
    onSaveComment,
    onCommentChange,
    openApprovalDialog,
    setOpenApprovalDialog,
    onApprovalConfirm,
    breadcrumbTitle,
    isDetailLoading,
    isAddPending,
    isUpdatePending,
    isDocumentTypeLoading,
    documentTypes,
    onActionClick,
    isAddSubmitForApprovalPending,
    postDocBtnDisable,
    keys,
    isAddCommentPending,
    onCommentClick,
    openViewCommentDialog,
    setOpenViewCommentDialog,
    lineItem,
    lineItemCommentText,
    allDisabled,
    invoiceAction,
    openApproveRejectDialog,
    setOpenApproveRejectDialog,
    isApproved,
    isApprovePending,
    isRejectPending,
    isPostingPending,
    onApproveClick,
    onRejectClick,
    onApproveConfirm,
    onRejectConfirm,
    onClose,
    openPostDialog,
    setOpenPostDialog,
    onPostConfirmation,
    invoiceId,
    openPaymentBlockDialog,
    setOpenPaymentBlockDialog,
    onSavePaymentBlock,
    enabledApproveReject,
    enableCheckBox,
    glAccount,
    isGLAccountLoading,
    paymentMethodOptions,
    isPaymentMethodLoading,
    paymentTermOptions,
    isPaymentTermLoading,
    onVendorChange,
    onDeleteLineItem,
    openDeleteLineItemDialog,
    setOpenDeleteLineItemDialog,
    onDeleteLineItemConfirmation,
    confirmationText,
    isAttachmentPending,
    isDeletePending,
    isAttachmentLoading,
    invoicePermission,
    isUpdatePermission,
    isCreate,
    onBaseLineDateChanged,
    lineItems,
    isEntryView,
    setIsEntryView,
    documentId,
    glAccountList,
    isPosted,
    isLoadingZoneList,
    zoneList,
    propertyBlock,
    isBlockLoading,
    propertyList,
    isPropertyLoading,
    onReassign,
    isReassignPending,
    isApprovalPending,
    onZoneChange,
    onPropertyChange,
  };
};

export default useAddEditInvoice;
